import React, { Fragment, useState, useEffect } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import PortalMenu from "../Common/PortalMenu";
import {
 Row,
 Col,
 Card,
 Table,
 Badge,
 Dropdown,
 Button,
 Spinner,
} from "react-bootstrap";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AppPagination from "../Common/AppPagination";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";

const OfficeExpenseList = () => {
 const navigate = useNavigate();
 const [expenses, setExpenses] = useState([]);
 const [offices, setOffices] = useState([]);
 const [expenseTypes, setExpenseTypes] = useState([]);
 const [loading, setLoading] = useState(false);
 const [pagination, setPagination] = useState({
 currentPage: 1,
 totalPages: 1,
 totalCount: 0,
 limit: 10,
 });
 const [searchParams] = useSearchParams();

 const officeFromUrl = searchParams.get("office") || "";
 const expenseTypeFromUrl = searchParams.get("expenseType") || "";
 const fromDateFromUrl = searchParams.get("fromDate") || "";
 const toDateFromUrl = searchParams.get("toDate") || "";

 const [filters, setFilters] = usePersistentFilters("office_expense_filters", {
 office: officeFromUrl,
 expenseType: expenseTypeFromUrl,
 fromDate: fromDateFromUrl,
 toDate: toDateFromUrl,
 page: 1,
 limit: 10,
 });

 useEffect(() => {
 setFilters((prev) => ({
 ...prev,
 office: officeFromUrl,
 expenseType: expenseTypeFromUrl || prev.expenseType,
 fromDate: fromDateFromUrl || prev.fromDate,
 toDate: toDateFromUrl || prev.toDate,
 }));
 }, [officeFromUrl, expenseTypeFromUrl, fromDateFromUrl, toDateFromUrl]);

 useEffect(() => {
 fetchOffices();
 fetchExpenseTypes();
 fetchExpenses();
 }, [filters, officeFromUrl]);

 const fetchOffices = async () => {
 try {
 const res = await api.get("/offices");
 if (res.data.success) setOffices(res.data.data);
 } catch (err) {
 console.error("Error fetching offices:", err);
 }
 };

 const fetchExpenseTypes = async () => {
 try {
 const res = await api.get("/expense-types?status=Active");
 if (res.data.success) setExpenseTypes(res.data.data);
 } catch (err) {
 console.error("Error fetching expense types:", err);
 }
 };

 const fetchExpenses = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams();
 params.append("excludeWorkOrder", "true");
 if (filters.office) params.append("office", filters.office);
 if (filters.expenseType) params.append("expenseType", filters.expenseType);
 if (filters.fromDate) params.append("fromDate", filters.fromDate);
 if (filters.toDate) params.append("toDate", filters.toDate);
 params.append("page", filters.page);
 params.append("limit", filters.limit);

 const res = await api.get(`/expenses?${params.toString()}`);
 if (res.data.success) {
 setExpenses(res.data.data || []);
 setPagination(res.data.pagination);
 }
 } catch (err) {
 console.error("Error fetching expenses:", err);
 } finally {
 setLoading(false);
 }
 };

 const [downloading, setDownloading] = useState(false);

 const handleDownloadPdf = async () => {
 try {
 setDownloading(true);
 const params = new URLSearchParams();
 params.append("excludeWorkOrder", "true");
 if (filters.office) params.append("office", filters.office);
 if (filters.expenseType) params.append("expenseType", filters.expenseType);
 if (filters.fromDate) params.append("fromDate", filters.fromDate);
 if (filters.toDate) params.append("toDate", filters.toDate);

 const token = localStorage.getItem("token"); 
 const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";
 const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
 const url = `${cleanBaseUrl}/api/expenses/download-pdf?${params.toString()}`;

 const response = await fetch(url, {
 method: 'GET',
 headers: {
 'Authorization': `Bearer ${token}`,
 'Accept': 'application/pdf'
 }
 });

 if (!response.ok) {
 const errorText = await response.text();
 let message = "Failed to generate PDF";
 try {
 const errJson = JSON.parse(errorText);
 message = errJson.message || message;
 } catch (e) {}
 throw new Error(message);
 }

 const blob = await response.blob();
 if (blob.size === 0) throw new Error("Received empty PDF file");

 const downloadUrl = window.URL.createObjectURL(blob);
 const link = document.createElement('a');
 link.href = downloadUrl;
 link.setAttribute('download', `Office_Expense_Report_${new Date().getTime()}.pdf`);
 document.body.appendChild(link);
 link.click();
 link.remove();
 window.setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 3000);
 } catch (err) {
 console.error("Error downloading PDF:", err);
 Swal.fire("Error", err.message || "Failed to download PDF.", "error");
 } finally {
 setDownloading(false);
 }
 };

 const handleDelete = async (id) => {
 const result = await Swal.fire({
 title: "Are you sure?",
 text: "You won't be able to revert this!",
 icon: "warning",
 showCancelButton: true,
 confirmButtonColor: "#3085d6",
 cancelButtonColor: "#d33",
 confirmButtonText: "Yes, delete it!",
 });

 if (result.isConfirmed) {
 try {
 const res = await api.delete(`/expenses/${id}`);
 if (res.data.success) {
 Swal.fire("Deleted!", "Office expense has been deleted.", "success");
 fetchExpenses();
 }
 } catch (err) {
 Swal.fire("Error", "Failed to delete office expense", "error");
 }
 }
 };

 const formatDate = (date) => {
 if (!date) return "—";
 return new Date(date).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 });
 };

 const formatCurrency = (amount) =>
 `₹ ${parseFloat(amount || 0).toLocaleString("en-IN", {
 minimumFractionDigits: 2,
 })}`;

 const getExpenseBills = (item) => {
 const bills = [
 ...(item.bill ? [item.bill] : []),
 ...(Array.isArray(item.bills) ? item.bills : []),
 ].filter(Boolean);

 return bills.filter((bill, index, self) => {
 const key = `${bill.filePath || ""}::${bill.fileName || ""}`;
 return index === self.findIndex((entry) => `${entry.filePath || ""}::${entry.fileName || ""}` === key);
 });
 };

 const svgMenu = (
 <svg width="20px" height="20px" viewBox="0 0 24 24">
 <circle fill="#000000" cx="5" cy="12" r="2"></circle>
 <circle fill="#000000" cx="12" cy="12" r="2"></circle>
 <circle fill="#000000" cx="19" cy="12" r="2"></circle>
 </svg>
 );

 return (
 <Fragment>
 <PageTitle activeMenu="Office Expense List" motherMenu="Accounts" />

 {/* Filter Bar */}
 <Row className="mb-3">
 <Col lg={12}>
 <Card style={{ minHeight: '160px' }}>
 <Card.Body>
 <Row className="g-3">
 <Col md={3}>
 <label className="form-label">Office</label>
 <select
 className="form-select form-select-sm"
 value={filters.office || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 office: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Offices</option>
 {offices.map((off) => (
 <option key={off._id} value={off._id}>
 {off.officeName}
 </option>
 ))}
 </select>
 </Col>
 <Col md={3}>
 <label className="form-label">Expense Type</label>
 <select
 className="form-select form-select-sm"
 value={filters.expenseType || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 expenseType: e.target.value,
 page: 1,
 }))
 }
 >
 <option value="">All Types</option>
 {expenseTypes.map((type) => (
 <option key={type._id} value={type.name}>
 {type.name}
 </option>
 ))}
 </select>
 </Col>
 <Col md={2}>
 <label className="form-label">From Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.fromDate || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 fromDate: e.target.value,
 page: 1,
 }))
 }
 />
 </Col>
 <Col md={2}>
 <label className="form-label">To Date</label>
 <input
 type="date"
 className="form-control form-control-sm"
 value={filters.toDate || ""}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 toDate: e.target.value,
 page: 1,
 }))
 }
 />
 </Col>
 <Col md={2}>
 <label className="form-label">Per Page</label>
 <select
 className="form-select form-select-sm"
 value={filters.limit || 10}
 onChange={(e) =>
 setFilters((p) => ({
 ...p,
 limit: Number(e.target.value),
 page: 1,
 }))
 }
 >
 <option value={10}>10</option>
 <option value={25}>25</option>
 <option value={50}>50</option>
 </select>
 </Col>
 <Col md={12} className="d-flex gap-2">
 <Button 
 variant="primary" 
 className="flex-grow-1"
 onClick={handleDownloadPdf}
 style={{ height: '45px' }}
 disabled={downloading}
 >
 {downloading ? (
 <>
 <Spinner
 as="span"
 animation="border"
 size="sm"
 role="status"
 aria-hidden="true"
 className="me-2"
 />
 Generating PDF...
 </>
 ) : (
 <>
 <i className="fa fa-file-pdf me-2"></i> Download PDF
 </>
 )}
 </Button>
 <Button 
 variant="outline-danger" 
 className="flex-grow-1"
 onClick={() => setFilters({
 office: "",
 expenseType: "",
 fromDate: "",
 toDate: "",
 page: 1,
 limit: 10,
 })}
 style={{ height: '45px' }}
 >
 <i className="fa fa-undo me-2"></i> Reset Filters
 </Button>
 </Col>
 </Row>
 </Card.Body>
 </Card>
 </Col>
 </Row>

 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title>Office Expense (Debit)</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
 Back
 </Button>
 <Button variant="primary" onClick={() => navigate("/debit-office/add")}>
 <i className="fa fa-plus me-2"></i> Add Expense
 </Button>
 </div>
 </Card.Header>
 <Card.Body>
 <div style={{ overflow: 'visible' }}>
 <Table className="w-100">
 <thead>
 <tr>
 <th>Sr. No.</th>
 <th>Date</th>
 <th>Type</th>
 <th>Amount</th>
 <th>Total (incl. GST)</th>
 <th>Payment Mode</th>
 <th>Paid To</th>
 <th>Office</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr>
 <td colSpan="9" className="text-center py-4">
 <Spinner animation="border" variant="primary" />
 </td>
 </tr>
 ) : expenses.length === 0 ? (
 <tr>
 <td colSpan="9" className="text-center py-4 text-muted">
 No office expenses found.
 </td>
 </tr>
 ) : (
 expenses.map((item, index) => (
 <tr key={item._id}>
 <td>{((pagination.currentPage || 1) - 1) * (pagination.limit || 10) + index + 1}</td>
 <td>{formatDate(item.expenseDate)}</td>
 <td>
                        <Badge bg="info">{item.expenseType}</Badge>
                        {item.recurringFrequency ? (
                          <Badge bg="primary" className="ms-1" style={{ fontSize: "10px" }}>
                            <i className="fa fa-sync-alt me-1"></i>{item.recurringFrequency}
                          </Badge>
                        ) : null}
                      </td>
 <td>{formatCurrency(item.amount)}</td>
 <td>{formatCurrency(item.totalAmount)}</td>
 <td>{item.paymentMode}</td>
 <td>{item.paidTo || "—"}</td>
 <td>{item.office?.officeName || "—"}</td>
 <td style={{ overflow: 'visible' }}>
 <Dropdown className="ms-auto">
 <Dropdown.Toggle
 as="div"
 className="btn-link p-0 no-caret"
 style={{ cursor: "pointer" }}
 >
 {svgMenu}
 </Dropdown.Toggle>
 <Dropdown.Menu align="end" as={PortalMenu}>
 {getExpenseBills(item).length > 0 && (
 <>
 {getExpenseBills(item).map((bill, billIndex) => (
 <Dropdown.Item
 key={`${bill.filePath || bill.fileName || billIndex}`}
 href={getFileUrl(bill.filePath)}
 target="_blank"
 rel="noreferrer"
 >
 <i className="fa fa-file-pdf me-2 text-danger"></i>
 {bill.fileName ? `View Bill ${billIndex + 1}` : "View Bill"}
 </Dropdown.Item>
 ))}
 </>
 )}
 <Dropdown.Item as={Link} to={`/debit-office/edit/${item._id}`}>
 <i className="fa fa-edit me-2 text-primary"></i> Edit
 </Dropdown.Item>
 <Dropdown.Item onClick={() => handleDelete(item._id)} className="text-danger">
 <i className="fa fa-trash me-2"></i> Delete
 </Dropdown.Item>
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>
 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={expenses.length}
 limit={pagination.limit}
 onPageChange={(page) => setFilters(p => ({ ...p, page: page }))}
 />
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default OfficeExpenseList;
